/**
 * Send chat message — what the client posts to `POST /api/chat/send`.
 *
 * Chat messages have four mutually-additive content axes (`content` text,
 * `imageUrl` attachment, `listing` card, `order` card). At least one must be
 * present; `roomName` is always required. The route enforces "at least one
 * content axis" via a refinement so the existing 400 response message
 * (`'roomName and (content or imageUrl or listing or order) required'`) is
 * preserved 1:1 as a Zod issue.
 *
 * Why the listing / order sub-schemas are loose (`passthrough`): the route
 * forwards both objects to `notify()` (push + email), which reads many display
 * fields (title, price, image_urls, currency, condition, …) that the boundary
 * doesn't need to validate. We pin the structural fields the route branches
 * on (`listing.id`, `order.id | order.orderNumber | order.sellerOrderId`) and
 * accept the rest as opaque additional properties — additive-safe for any
 * future display field mobile adds.
 *
 * Mobile-consumed (api-messaging.ts sendMessage).
 *
 * @see app/api/chat/send/route.ts — handler
 * @see apps/mobile/lib/api-messaging.ts sendMessage — mobile caller
 * @see .tondocs/payments/API_REQUEST_BOUNDARY_AUDIT.md H-10
 */

import { z } from "zod";
import { pinned } from "./pin";

/**
 * `listing` payload — `listing.id` is the only field the route branches on.
 * Display fields (title, price, currency, image_urls, condition, grade,
 * location, …) are passed through to `notify()`.
 *
 * `id` is `.nullish()`: producers (web `realtime-chat.tsx` + mobile
 * `ChatListingPayload`) build the listing card from row objects whose `id`
 * field can be `null` when the row hasn't materialised yet (private-listing
 * placeholder, etc.). The route's `if (listing?.id)` truthy check already drops
 * null, so accepting it is wire-truthful — additive only.
 */
const ChatListingPayloadSchema = z
  .object({
    id: z.string().min(1).nullish(),
  })
  .loose();

/**
 * `order` payload — at least one identifier (`id`, `orderNumber`, or
 * `sellerOrderId`) is required for the route to resolve the order row. The
 * route handles missing-identifier via "drop the order card silently"; this
 * schema accepts any of the three.
 *
 * Display fields (status, total, itemCount, firstItemImage/Title, buyerName,
 * sellerName, imageUrls, items[], etc.) are passed through to `notify()`.
 *
 * All four identifiers are `.nullish()`: producers (web `realtime-chat.tsx`
 * `persistOneOrder` + `OrderChatPreview.tsx` + mobile `ChatOrderPreview`) ship
 * literal `null` for whichever identifier they don't have on hand (e.g.
 * `sellerOrderId: sellerOrderId ?? null`). The route already coalesces by
 * truthy-checking each, so accepting null is additive.
 */
const ChatOrderPayloadSchema = z
  .object({
    id: z.string().nullish(),
    orderId: z.string().nullish(),
    orderNumber: z.string().nullish(),
    sellerOrderId: z.string().nullish(),
  })
  .loose();

export interface ChatSendRequest {
  /**
   * Canonical room name. The route expects a colon-joined list of user ids
   * (e.g. `userA:userB`); it normalises into sorted order server-side.
   */
  roomName: string;
  /** Message text. Optional when an image / listing / order is present. */
  content?: string;
  /** Image URL string. Optional. */
  imageUrl?: string;
  /** Listing card metadata. Optional. */
  listing?: z.infer<typeof ChatListingPayloadSchema>;
  /** Order card metadata. Optional. */
  order?: z.infer<typeof ChatOrderPayloadSchema>;
  /** Server-attachment id (image upload pointer). Optional. */
  attachmentId?: string;
  /** Optional camelCase variant the server also reads. */
  attachment_id?: string;
  /** Parent message id for threaded replies. Optional. */
  replyToMessageId?: string;
  /** Snake_case alias the server also reads. */
  reply_to_message_id?: string;
  /**
   * When true, skip the first-inquiry email triggered by `listing`. Optional;
   * the route also reads the snake_case form.
   */
  skipListingEmail?: boolean;
  skip_listing_email?: boolean;
}

export const ChatSendRequestSchema = pinned<ChatSendRequest>()(
  z
    .object({
      roomName: z.string().min(1),
      content: z.string().optional(),
      imageUrl: z.string().optional(),
      listing: ChatListingPayloadSchema.optional(),
      order: ChatOrderPayloadSchema.optional(),
      attachmentId: z.string().optional(),
      attachment_id: z.string().optional(),
      replyToMessageId: z.string().uuid().optional(),
      reply_to_message_id: z.string().uuid().optional(),
      skipListingEmail: z.boolean().optional(),
      skip_listing_email: z.boolean().optional(),
    })
    .refine(
      (body) => {
        const hasText =
          typeof body.content === "string" && body.content.trim() !== "";
        const hasImage = !!body.imageUrl;
        const hasListing = !!body.listing;
        const hasOrder = !!body.order;
        return hasText || hasImage || hasListing || hasOrder;
      },
      {
        message:
          "roomName and (content or imageUrl or listing or order) required",
        path: ["content"],
      },
    ),
);
